import http from "../http-common";
import { AxiosResponse } from "axios";

const convertResponseToSingleObjectOrNull = (response: AxiosResponse) => {
  if (Array.isArray(response.data) && response.data.length) {
    return response.data[0];
  }
  return null;
};

class CategoriesDataService {
  getCategoryList(): Promise<AxiosResponse<[Category]>> {
    return http.get<[Category]>("/categorylist");
  }

  getSubcategoryList(): Promise<AxiosResponse<[Subcategory]>> {
    return http.get<[Subcategory]>("/subcategorylist");
  }

  getCategoriesExplorerList(): Promise<AxiosResponse<[CategorySelectorRow]>> {
    return http.get<[CategorySelectorRow]>("/categories");
  }

  getCategoryDetails(
    categoryId: string,
    subcategoryId?: string
  ): Promise<CategorySelectorRow | null> {
    let url = `/category-details?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http
      .get<CategorySelectorRow | null>(url)
      .then(convertResponseToSingleObjectOrNull);
  }

  getArticleCounts(
    categoryId: string,
    subcategoryId?: string
  ): Promise<AxiosResponse<ArticleCount[]>> {
    let url = `/articles?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http.get<ArticleCount[]>(url);
  }

  getSubcategories(
    categoryId: string
  ): Promise<AxiosResponse<CategoryViewDataRow[]>> {
    return http.get<CategoryViewDataRow[]>(`/subcategories/${categoryId}`);
  }

  getKeywords(
    categoryId: string,
    subcategoryId?: string
  ): Promise<AxiosResponse<KeywordsForCategoryRow[]>> {
    let url = `/keywords?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http.get<KeywordsForCategoryRow[]>(url);
  }

  getJournals(
    categoryId: string,
    subcategoryId?: string
  ): Promise<AxiosResponse<CategoryViewDataRow[]>> {
    let url = `/journals?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http.get<CategoryViewDataRow[]>(url);
  }

  getDocumentTypes(
    categoryId: string,
    subcategoryId?: string
  ): Promise<AxiosResponse<CategoryViewDataRow[]>> {
    let url = `/documents?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http.get<CategoryViewDataRow[]>(url);
  }

  getLanguages(
    categoryId: string,
    subcategoryId?: string
  ): Promise<AxiosResponse<CategoryViewDataRow[]>> {
    let url = `/languages?id=${categoryId}`;
    if (subcategoryId) {
      url = url + `&sid=${subcategoryId}`;
    }
    return http.get<CategoryViewDataRow[]>(url);
  }

  getArticlesComparison(
    categoryIds: Category[],
    subcategoryIds: Subcategory[]
  ): Promise<AxiosResponse<ComparisonArticles>> {
    const url = `/comparison-articles?ids=${JSON.stringify(
      categoryIds
    )}&sids=${JSON.stringify(subcategoryIds)}`;
    return http.get<ComparisonArticles>(url);
  }

  getKeywordComparison(
    categoryIds: Category[],
    subcategoryIds: Subcategory[],
    dateRange: DateRange
  ): Promise<AxiosResponse<KeywordComparisonResponse>> {
    const url = `/comparison-keywords?ids=${JSON.stringify(
      categoryIds
    )}&sids=${JSON.stringify(subcategoryIds)}&dateRange=${JSON.stringify(
      dateRange
    )}`;
    return http.get<KeywordComparisonResponse>(url);
  }

  getJournalsComparison(
    categoryIds: Category[],
    subcategoryIds: Subcategory[],
    dateRange: DateRange
  ): Promise<AxiosResponse<ComparisonResponse>> {
    const url = `/comparison-journals?ids=${JSON.stringify(
      categoryIds
    )}&sids=${JSON.stringify(subcategoryIds)}&dateRange=${JSON.stringify(
      dateRange
    )}`;
    return http.get<ComparisonResponse>(url);
  }

  getDocumentTypesComparison(
    categoryIds: Category[],
    subcategoryIds: Subcategory[],
    dateRange: DateRange
  ): Promise<AxiosResponse<ComparisonResponse>> {
    const url = `/comparison-documents?ids=${JSON.stringify(
      categoryIds
    )}&sids=${JSON.stringify(subcategoryIds)}&dateRange=${JSON.stringify(
      dateRange
    )}`;
    return http.get<ComparisonResponse>(url);
  }

  getLanguagesComparison(
    categoryIds: Category[],
    subcategoryIds: Subcategory[],
    dateRange: DateRange
  ): Promise<AxiosResponse<ComparisonResponse>> {
    const url = `/comparison-languages?ids=${JSON.stringify(
      categoryIds
    )}&sids=${JSON.stringify(subcategoryIds)}&dateRange=${JSON.stringify(
      dateRange
    )}`;
    return http.get<ComparisonResponse>(url);
  }
}

export interface DateRange {
  start: number;
  end: number;
}

export interface ComparisonArticles {
  categories: [
    {
      category: Category;
      values: ArticleCount[];
    }
  ];
  subcategories: [
    {
      subcategory: Subcategory;
      values: ArticleCount[];
    }
  ];
}

export interface ComparisonResponse {
  categories: [
    {
      category: Category;
      values: CategoryViewDataRow[];
    }
  ];
  subcategories: [
    {
      subcategory: Subcategory;
      values: CategoryViewDataRow[];
    }
  ];
}

export interface KeywordComparisonResponse {
  dateRange: DateRange;
  categories: [
    {
      category: Category;
      values: KeywordsForCategoryRow[];
    }
  ];
  subcategories: [
    {
      subcategory: Subcategory;
      values: KeywordsForCategoryRow[];
    }
  ];
}

export interface Category {
  id: string;
  field: string;
}

export interface Subcategory {
  parentId: string;
  id: string;
  field: string;
}

export interface CategorySelectorRow {
  section_letter: string;
  section_heading: string;
  subsection_letter?: string;
  subsection_heading?: string;
}

export interface ArticleCount {
  publication_year: number;
  article_count: number;
}

export interface KeywordsForCategoryRow extends CategoryViewDataRow {
  generic: boolean;
}

export interface CategoryViewDataRow {
  id: string;
  field: string;
  amount: number;
}

export interface SubcategoryViewDataRow {
  id: string;
  parentId: string;
  field: string;
  amount: number;
}

export interface KeywordSearchResult {
  keyword: string;
  thesaurusId: string;
  generic: boolean;
}

export default new CategoriesDataService();
